import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Brain, Zap, Award, Users, Clock, ChevronDown, ChevronUp, Menu, X, Mail, MapPin, User, Newspaper, ExternalLink, CalendarDays, Timer, Star, Quote, GraduationCap, FileText, Globe } from "lucide-react";
import { usePageTracking } from "../hooks/useAnalytics";
import PopupManager from "../components/PopupManager";
import LandingDemoPopup from "../components/LandingDemoPopup";
import ShansoLearnBanner from "../components/ShansoLearnBanner";
import { TutorPromoCard } from "../components/tutor/TutorPromoCard";
import HeroVaultVideo from "../components/HeroVaultVideo";
import StickyShansoLearnPromoBar from "../components/StickyShansoLearnPromoBar";
import {
  ScrollProgress, TypewriterText, TiltCard, MagneticButton, FloatingParticles,
  AnimatedGrid, TextReveal, ScrollSection, staggerContainer, staggerItem,
  GradientDivider, GradientText, MorphingBlobs, ConstellationNetwork
} from "../components/Animations";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: "easeOut" }
  })
};

const HERO_BG = "https://images.unsplash.com/photo-1746421094550-8ca6940c2f6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const FEATURES = [
  { icon: Brain, title: "Expert-Led AI Curriculum", desc: "Courses crafted by industry practitioners with real-world AI deployment experience." },
  { icon: Zap, title: "Hands-On Projects", desc: "Build production-grade AI models with live datasets and real business problems." },
  { icon: Award, title: "Verified Certificates", desc: "Earn industry-recognized certificates that validate your AI expertise." },
  { icon: Users, title: "Community Access", desc: "Join a network of AI professionals and get mentorship from experts." }
];

function Navbar({ onScrollToTrainings, hasTickerAbove }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(-1);
  const navigate = useNavigate();

  const navItems = [
    { label: "Home", action: () => { if (window.location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" }); else navigate("/"); }, type: "button", testid: "nav-home-link" },
    { label: "Courses", action: onScrollToTrainings, type: "button" },
    { label: "ShansoLearn", href: "/courses", testid: "nav-shanso-learn-link", gradient: true, badge: "NEW" },
    { label: "AI Tutor", href: "/tutor", testid: "nav-tutor-link", gradient: true, badge: "NEW" },
    { label: "Document Purchase", href: "#books", testid: "nav-books-link" },
    { label: "Website Services", href: "/website-services", testid: "nav-website-services-link" },
    { label: "AI Tools", href: "/ai-tools", testid: "nav-ai-tools-link", gradient: true, badge: "NEW" },
    { label: "Pricing", href: "/pricing", testid: "nav-pricing-link" },
    { label: "Become An AI Founder", href: "/become-ai-founder", testid: "nav-ai-founder-link", gradient: true },
    { label: "Why Us", href: "#features" },
    { label: "AI News", href: "#ai-news", testid: "nav-ai-news-link" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Contact", href: "#contact" },
    { label: "Admin", action: () => navigate("/admin/login"), type: "button", testid: "admin-login-nav-btn" },
  ];

  return (
    <nav className={`fixed left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A] transition-all ${hasTickerAbove ? "top-9" : "top-0"}`}>
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.03 }} transition={{ duration: 0.2 }}>
          <div className="w-8 h-8 bg-[#00F0FF] rounded-sm flex items-center justify-center">
            <Brain size={18} className="text-black" />
          </div>
          <span className="font-outfit font-bold text-[#00F0FF] text-lg tracking-tight">ShansoAI<span className="text-white">Hub</span></span>
        </motion.div>

        {/* Desktop Nav — Magnetic Pull + Focus Lens */}
        <div className="hidden md:flex items-center gap-1" onMouseLeave={() => setHoveredIdx(-1)}>
          {navItems.map((item, i) => {
            const isHovered = hoveredIdx === i;
            const isNeighbor = hoveredIdx >= 0 && Math.abs(hoveredIdx - i) === 1;
            const isFar = hoveredIdx >= 0 && !isHovered && !isNeighbor;

            const scale = isHovered ? 1.12 : isNeighbor ? 0.95 : isFar ? 0.92 : 1;
            const opacity = isHovered ? 1 : isNeighbor ? 0.7 : isFar ? 0.5 : 0.7;

            const Tag = item.type === "button" ? "button" : "a";
            const linkProps = item.type === "button"
              ? { onClick: item.action }
              : { href: item.href };

            return (
              <MagneticNavItem key={i} scale={scale}>
                <Tag
                  {...linkProps}
                  data-testid={item.testid}
                  onMouseEnter={() => setHoveredIdx(i)}
                  className="relative px-3 py-1.5 text-sm font-dm-sans transition-all duration-200 rounded-md whitespace-nowrap"
                  style={{
                    opacity,
                    color: isHovered ? "#00F0FF" : "#c0d0e0",
                    fontWeight: isHovered ? 700 : (item.gradient ? 700 : 600),
                    textShadow: isHovered ? "0 0 15px rgba(0,240,255,0.6)" : "none",
                  }}
                >
                  {item.label}
                  {item.badge && (
                    <span className="absolute -top-1.5 -right-2 bg-[#00F0FF] text-[#050505] text-[8px] font-bold px-1.5 py-0.5 rounded-full leading-none" style={{ WebkitTextFillColor: "initial" }}>{item.badge}</span>
                  )}
                  {/* Hover glow underline */}
                  <motion.div
                    className="absolute bottom-0 left-1/2 h-[2px] bg-[#00F0FF] rounded-full"
                    initial={{ width: 0, x: "-50%" }}
                    animate={{
                      width: isHovered ? "70%" : "0%",
                      x: "-50%",
                    }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    style={{ boxShadow: isHovered ? "0 0 8px #00F0FF" : "none" }}
                  />
                </Tag>
              </MagneticNavItem>
            );
          })}

          <motion.button
            data-testid="browse-courses-nav-btn"
            onClick={onScrollToTrainings}
            className="bg-[#00F0FF] text-black font-bold text-sm px-5 py-2 hover:bg-white transition-colors duration-300 font-dm-sans ml-2"
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
          >Browse Courses</motion.button>
        </div>

        <button className="md:hidden text-white" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {menuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-[#0A0A0A] border-b border-[#27272A] px-6 py-4 flex flex-col gap-4"
        >
          <button onClick={() => { navigate("/"); setMenuOpen(false); }} className="text-[#00F0FF] hover:text-white text-sm font-semibold text-left" data-testid="nav-home-link-mobile">Home</button>
          <button onClick={() => { onScrollToTrainings(); setMenuOpen(false); }} className="text-[#A1A1AA] hover:text-white text-sm text-left">Courses</button>
          <a href="/courses" className="text-sm font-semibold" data-testid="nav-shanso-learn-link-mobile" style={{background: "linear-gradient(90deg, #00F0FF, #7000FF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"}} onClick={() => setMenuOpen(false)}>ShansoLearn <span className="text-[9px] bg-[#00F0FF] text-black px-1.5 py-0.5 rounded-full ml-1 font-bold" style={{WebkitTextFillColor: "initial"}}>NEW</span></a>
          <a href="/tutor" className="text-sm font-semibold" data-testid="nav-tutor-link-mobile" style={{background: "linear-gradient(90deg, #00F0FF, #7000FF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"}} onClick={() => setMenuOpen(false)}>AI Tutor <span className="text-[9px] bg-[#00F0FF] text-black px-1.5 py-0.5 rounded-full ml-1 font-bold" style={{WebkitTextFillColor: "initial"}}>NEW</span></a>
          <a href="#books" className="text-[#A1A1AA] hover:text-white text-sm" onClick={() => setMenuOpen(false)} data-testid="nav-books-link-mobile">Document Purchase</a>
          <a href="/website-services" className="text-[#A1A1AA] hover:text-white text-sm" data-testid="nav-website-services-link-mobile">Website Services</a>
          <a href="/ai-tools" className="text-sm font-semibold" data-testid="nav-ai-tools-link-mobile" style={{background: "linear-gradient(90deg, #00F0FF, #7000FF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"}} onClick={() => setMenuOpen(false)}>AI Tools <span className="text-[9px] bg-[#00F0FF] text-black px-1.5 py-0.5 rounded-full ml-1 font-bold" style={{WebkitTextFillColor: "initial"}}>NEW</span></a>
          <a href="/become-ai-founder" className="text-sm font-semibold" data-testid="nav-ai-founder-link-mobile" style={{background: "linear-gradient(135deg, #00F0FF, #7000FF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"}} onClick={() => setMenuOpen(false)}>Become An AI Founder</a>
          <a href="#features" className="text-[#A1A1AA] hover:text-white text-sm" onClick={() => setMenuOpen(false)}>Why Us</a>
          <a href="#ai-news" className="text-[#A1A1AA] hover:text-white text-sm" onClick={() => setMenuOpen(false)} data-testid="nav-ai-news-link-mobile">AI News</a>
          <a href="#contact" className="text-[#A1A1AA] hover:text-white text-sm" onClick={() => setMenuOpen(false)}>Contact</a>
          <button onClick={() => navigate("/admin/login")} className="text-[#A1A1AA] hover:text-white text-sm text-left">Admin</button>
        </motion.div>
      )}
    </nav>
  );
}

/* ─── Magnetic Nav Item wrapper ─── */
function MagneticNavItem({ children, scale }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });

  const handleMouse = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) * 0.2);
    y.set((e.clientY - rect.top - rect.height / 2) * 0.3);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ x: springX, y: springY }}
      animate={{ scale }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
    >
      {children}
    </motion.div>
  );
}

// Compact Countdown Timer for Course Cards
function CompactCountdown({ targetDate, targetTime }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      
      // Parse date and time properly
      if (!targetDate) {
        setIsExpired(true);
        return;
      }
      
      // Get date part (handle both YYYY-MM-DD and ISO formats)
      const datePart = targetDate.includes('T') ? targetDate.split('T')[0] : targetDate;
      
      // Get time part - default to 00:00 if not provided
      let timePart = '00:00';
      if (targetTime) {
        // Convert 12hr format to 24hr if needed
        if (targetTime.toUpperCase().includes('AM') || targetTime.toUpperCase().includes('PM')) {
          const match = targetTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          if (match) {
            let hours = parseInt(match[1]);
            const minutes = match[2];
            const period = match[3].toUpperCase();
            if (period === 'PM' && hours !== 12) hours += 12;
            if (period === 'AM' && hours === 12) hours = 0;
            timePart = `${String(hours).padStart(2, '0')}:${minutes}`;
          }
        } else {
          timePart = targetTime;
        }
      }
      
      // Create target date - use local timezone
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);
      const target = new Date(year, month - 1, day, hours, minutes, 0);
      
      const diff = target - now;

      if (diff <= 0) {
        setIsExpired(true);
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate, targetTime]);

  if (isExpired) {
    return (
      <div className="bg-gradient-to-r from-zinc-700/50 to-zinc-600/50 border border-zinc-500/40 p-3 mt-3">
        <div className="flex items-center justify-center gap-2">
          <Timer size={14} className="text-zinc-400" />
          <span className="text-zinc-400 text-sm font-bold uppercase tracking-wide">Registration Closed</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-violet-600/30 to-purple-600/30 border border-violet-500/40 p-3 mt-3">
      <div className="flex items-center gap-1.5 mb-2">
        <Timer size={12} className="text-violet-400" />
        <span className="text-violet-300 text-[10px] font-bold uppercase tracking-wide">Registration Closes In</span>
      </div>
      <div className="flex justify-center gap-2">
        <div className="text-center">
          <div className="bg-black/50 text-white font-outfit font-bold text-sm px-2 py-1 min-w-[28px]">
            {String(timeLeft.days).padStart(2, '0')}
          </div>
          <div className="text-violet-400 text-[8px] mt-0.5 font-dm-sans uppercase">Days</div>
        </div>
        <div className="text-violet-400 font-bold text-sm leading-6">:</div>
        <div className="text-center">
          <div className="bg-black/50 text-white font-outfit font-bold text-sm px-2 py-1 min-w-[28px]">
            {String(timeLeft.hours).padStart(2, '0')}
          </div>
          <div className="text-violet-400 text-[8px] mt-0.5 font-dm-sans uppercase">Hrs</div>
        </div>
        <div className="text-violet-400 font-bold text-sm leading-6">:</div>
        <div className="text-center">
          <div className="bg-black/50 text-white font-outfit font-bold text-sm px-2 py-1 min-w-[28px]">
            {String(timeLeft.minutes).padStart(2, '0')}
          </div>
          <div className="text-violet-400 text-[8px] mt-0.5 font-dm-sans uppercase">Min</div>
        </div>
        <div className="text-violet-400 font-bold text-sm leading-6">:</div>
        <div className="text-center">
          <div className="bg-black/50 text-white font-outfit font-bold text-sm px-2 py-1 min-w-[28px]">
            {String(timeLeft.seconds).padStart(2, '0')}
          </div>
          <div className="text-violet-400 text-[8px] mt-0.5 font-dm-sans uppercase">Sec</div>
        </div>
      </div>
    </div>
  );
}

function CourseCard({ training, index }) {
  const navigate = useNavigate();
  const [posterImage, setPosterImage] = useState(null);
  const seatsLeft = training.seats - (training.seats_filled || 0);
  const isFull = seatsLeft <= 0;
  const fillRate = training.seats > 0 ? ((training.seats_filled || 0) / training.seats) * 100 : 0;
  
  // Offer calculations
  const hasOffer = training.offer_enabled && training.offer_percentage && training.offer_end_date;
  const offerEndDate = hasOffer ? new Date(training.offer_end_date) : null;
  const isOfferValid = hasOffer && offerEndDate && offerEndDate >= new Date();
  const offerPrice = isOfferValid ? Math.round(training.price * (1 - training.offer_percentage / 100)) : training.price;
  
  // Lazy load poster image if training has one
  useEffect(() => {
    if (training.has_poster) {
      axios.get(`${API}/trainings/${training.id}/poster`)
        .then(res => setPosterImage(res.data.poster_image_data))
        .catch(() => {}); // Silently fail, will use fallback
    }
  }, [training.id, training.has_poster]);
  
  // Use lazy-loaded poster, then fall back to image_url, then default
  const courseImage = posterImage || training.image_url || "https://images.unsplash.com/photo-1646583288948-24548aedffd8?w=600&h=300&fit=crop";

  // Smart seat display text
  const getSeatDisplayText = () => {
    if (isFull) return { text: "Fully Booked", color: "text-red-400", icon: "🔴" };
    if (fillRate >= 80) return { text: `Only ${seatsLeft} left!`, color: "text-red-400", icon: "🔥" };
    if (fillRate >= 50) return { text: "Filling Fast", color: "text-amber-400", icon: "⚡" };
    return { text: "Limited Batch", color: "text-[#00F0FF]", icon: "" };
  };
  
  const seatDisplay = getSeatDisplayText();

  // Format date and time
  const formatDateTime = () => {
    if (!training.date) return null;
    
    // Parse date - handle both "YYYY-MM-DD" and ISO datetime formats
    const dateStr = training.date;
    let year, month, day;
    
    if (dateStr.includes('T')) {
      // ISO datetime format - parse as UTC to avoid timezone shifts
      const parts = dateStr.split('T')[0].split('-');
      year = parts[0];
      month = new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, 1)).toLocaleString('en-US', { month: 'short' });
      day = parts[2].padStart(2, '0');
    } else {
      // Simple date format YYYY-MM-DD
      const parts = dateStr.split('-');
      year = parts[0];
      month = new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, 1)).toLocaleString('en-US', { month: 'short' });
      day = parts[2].padStart(2, '0');
    }
    
    const formattedDate = `${year}-${month}-${day}`;
    
    // Use the separate time field if available, otherwise show TBA
    let timeStr = training.time || 'TBA';
    // Ensure time has AM/PM - if it's in 24hr format, convert it
    if (timeStr !== 'TBA' && !timeStr.toUpperCase().includes('AM') && !timeStr.toUpperCase().includes('PM')) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      timeStr = `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
    }
    
    return { date: formattedDate, time: timeStr };
  };
  
  const dateTime = formatDateTime();

  return (
    <motion.div
      custom={index}
      variants={staggerItem}
      data-testid={`course-card-${training.id}`}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.3 }}
    >
      <TiltCard intensity={6} className="bg-zinc-900/50 border border-zinc-800 hover:border-[#00F0FF]/50 transition-all duration-500 group relative overflow-hidden cursor-pointer h-full"
        onClick={() => navigate(`/trainings/${training.id}`)}>
        {/* Hover glow */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-t from-[#00F0FF]/5 via-transparent to-transparent" />
      <div className="relative overflow-hidden h-44">
        <img
          src={courseImage}
          alt={training.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />
        
        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          <span className={`text-xs font-bold px-2 py-1 ${training.status === 'completed' ? 'bg-zinc-600 text-zinc-300' : 'bg-[#00F0FF] text-black'}`}>
            {training.status === 'completed' ? 'COMPLETED' : 'ENROLLING'}
          </span>
        </div>
        
        {/* Offer Badge */}
        {isOfferValid && (
          <div className="absolute top-3 left-3">
            <span className="text-xs font-bold px-2 py-1 bg-red-500 text-white animate-pulse">
              {training.offer_percentage}% OFF
            </span>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-outfit font-bold text-white text-lg leading-tight mb-2 group-hover:text-[#00F0FF] transition-colors">{training.title}</h3>
        <p className="text-[#A1A1AA] text-sm mb-4 line-clamp-2 font-dm-sans">{training.description}</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {dateTime && (
            <div className="flex items-center gap-1.5 text-xs text-[#A1A1AA]" data-testid={`course-date-${training.id}`}>
              <CalendarDays size={13} className="text-[#00F0FF]" />
              <span>{dateTime.date}, {dateTime.time}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-[#A1A1AA]">
            <Clock size={13} className="text-[#00F0FF]" />
            <span>{training.duration}</span>
          </div>
          <div className={`flex items-center gap-1.5 text-xs ${seatDisplay.color} ${dateTime ? '' : ''}`}>
            <Users size={13} />
            <span>{seatDisplay.icon} {seatDisplay.text}</span>
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
          <div>
            {isOfferValid ? (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-[#00F0FF] font-outfit font-bold text-xl">&#8377;{offerPrice.toLocaleString('en-IN')}</span>
                  <span className="text-zinc-500 font-outfit text-sm line-through">&#8377;{training.price.toLocaleString('en-IN')}</span>
                </div>
                <span className="text-red-400 text-[10px] font-dm-sans">
                  Ends {offerEndDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            ) : (
              <span className="text-[#00F0FF] font-outfit font-bold text-xl">&#8377;{training.price.toLocaleString('en-IN')}</span>
            )}
          </div>
          <button
            data-testid={`enroll-btn-${training.id}`}
            className={`flex items-center gap-1.5 text-sm font-bold px-4 py-2 transition-colors duration-200 ${isFull ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed' : 'bg-white text-black hover:bg-[#00F0FF]'}`}
            onClick={(e) => { e.stopPropagation(); if (!isFull) navigate(`/trainings/${training.id}`); }}
            disabled={isFull}
          >
            {isFull ? 'Full' : 'Enroll'} <ArrowRight size={14} />
          </button>
        </div>
        
        {/* Countdown Timer - Only show for active trainings that aren't full */}
        {training.status !== 'completed' && !isFull && (
          <CompactCountdown targetDate={training.date} targetTime={training.time} />
        )}
      </div>
      </TiltCard>
    </motion.div>
  );
}

// Book Card Component
function BookCard({ book, index }) {
  const navigate = useNavigate();
  const discountedPrice = book.discount_percentage > 0 
    ? Math.round(book.price * (1 - book.discount_percentage / 100))
    : book.price;
  
  const isFree = discountedPrice === 0 || book.price === 0;

  const handleBuy = () => {
    navigate(`/book/${book.id}/purchase`);
  };

  const handleFreeDownload = () => {
    // Direct download for free documents
    window.open(`${API}/books/free/download/${book.id}`, '_blank');
  };

  const handleFreeRead = () => {
    // Navigate to free reader page
    navigate(`/book/${book.id}/read-free`);
  };

  return (
    <motion.div
      custom={index}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="group bg-[#111] border border-zinc-800 overflow-hidden hover:border-[#00F0FF]/40 transition-all duration-300"
      data-testid={`book-card-${book.id}`}
    >
      {/* Book Cover */}
      <div className="relative h-56 bg-gradient-to-br from-[#00F0FF]/10 to-[#7000FF]/10 overflow-hidden">
        <img 
          src={`${API}/books/${book.id}/cover`}
          alt={book.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        {/* Badges */}
        {isFree ? (
          <div className="absolute top-3 left-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1">
            FREE
          </div>
        ) : book.discount_percentage > 0 && (
          <div className="absolute top-3 left-3 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-2 py-1">
            {book.discount_percentage}% OFF
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-5">
        <h3 className="font-outfit font-bold text-white text-lg leading-tight mb-1 line-clamp-2">{book.title}</h3>
        <p className="text-[#A1A1AA] text-sm font-dm-sans mb-2">by {book.author}</p>
        <p className="text-zinc-500 text-sm font-dm-sans line-clamp-2 mb-4">{book.description}</p>
        
        {/* Price and Actions */}
        <div className="flex flex-col gap-3">
          {/* Price Display */}
          <div className="flex items-center gap-2">
            {isFree ? (
              <span className="text-xl font-bold text-green-400 font-outfit">FREE</span>
            ) : book.discount_percentage > 0 ? (
              <>
                <span className="text-xl font-bold text-[#00F0FF] font-outfit">₹{discountedPrice}</span>
                <span className="text-sm text-zinc-500 line-through">₹{book.price}</span>
              </>
            ) : (
              <span className="text-xl font-bold text-white font-outfit">₹{book.price}</span>
            )}
          </div>
          
          {/* Action Buttons */}
          {isFree ? (
            <div className="flex gap-2">
              <button
                onClick={handleFreeDownload}
                data-testid={`free-download-btn-${book.id}`}
                className="flex-1 bg-green-500 text-white font-bold text-sm px-3 py-2 hover:bg-green-600 transition-colors flex items-center justify-center gap-1"
              >
                <ArrowRight size={14} className="rotate-90" /> Download PDF
              </button>
              <button
                onClick={handleFreeRead}
                data-testid={`free-read-btn-${book.id}`}
                className="flex-1 bg-[#00F0FF] text-black font-bold text-sm px-3 py-2 hover:bg-white transition-colors flex items-center justify-center gap-1"
              >
                <Clock size={14} /> Read Online
              </button>
            </div>
          ) : (
            <button
              onClick={handleBuy}
              data-testid={`buy-book-btn-${book.id}`}
              className="w-full bg-[#00F0FF] text-black font-bold text-sm px-4 py-2 hover:bg-white transition-colors flex items-center justify-center gap-1"
            >
              Buy Now <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

const TICKER_ICONS = {
  rocket: "🚀", calendar: "📅", star: "⭐", zap: "⚡", megaphone: "📢", gift: "🎁", alert: "🔔", fire: "🔥"
};

function NewsTicker({ announcements, onDismiss }) {
  const [paused, setPaused] = useState(false);

  const items = announcements.map(a => ({
    text: a.text,
    icon: a.icon && TICKER_ICONS[a.icon] ? TICKER_ICONS[a.icon] : "",
    link_url: a.link_url,
    link_text: a.link_text
  }));

  // Duplicate items for seamless loop
  const duped = [...items, ...items, ...items];

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#020210] via-[#050520] to-[#020210] border-b border-[#00F0FF]/10"
      data-testid="news-ticker"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-center h-9">
        <div className="overflow-hidden flex-1 relative">
          <div
            className={`flex whitespace-nowrap ${paused ? "" : "animate-marquee"}`}
            style={{ animationPlayState: paused ? "paused" : "running" }}
          >
            {duped.map((item, idx) => (
              <span key={idx} className="inline-flex items-center mx-8 text-sm font-dm-sans">
                {item.icon && <span className="mr-1.5">{item.icon}</span>}
                <span className="text-[#c0c0d0]">{item.text}</span>
                {item.link_url && item.link_text && (
                  <a
                    href={item.link_url}
                    className="text-[#00F0FF] ml-1.5 hover:underline font-semibold"
                    onClick={e => e.stopPropagation()}
                  >
                    {item.link_text} →
                  </a>
                )}
              </span>
            ))}
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center text-zinc-600 hover:text-white transition-colors"
          data-testid="dismiss-ticker"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [trainings, setTrainings] = useState([]);
  const [news, setNews] = useState([]);
  const [books, setBooks] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllTrainingReviews, setShowAllTrainingReviews] = useState(false);
  const [showAllDocumentReviews, setShowAllDocumentReviews] = useState(false);
  const [showAllWebsiteReviews, setShowAllWebsiteReviews] = useState(false);
  const [activeTestimonialTab, setActiveTestimonialTab] = useState("training");
  const [announcements, setAnnouncements] = useState([]);
  const [tickerDismissed, setTickerDismissed] = useState(false);
  const navigate = useNavigate();
  
  // Track page view
  usePageTracking();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/trainings`),
      axios.get(`${API}/news`),
      axios.get(`${API}/books`),
      axios.get(`${API}/testimonials/featured`),
      axios.get(`${API}/announcements`)
    ]).then(([trainingsRes, newsRes, booksRes, testimonialsRes, annRes]) => {
      setTrainings(trainingsRes.data);
      setNews(newsRes.data);
      setBooks(booksRes.data);
      setTestimonials(testimonialsRes.data);
      setAnnouncements(annRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const scrollToTrainings = () => {
    document.getElementById("courses")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="marketing-bg min-h-screen">
      <PopupManager />
      <LandingDemoPopup />
      <StickyShansoLearnPromoBar />
      {/* News Ticker */}
      {announcements.length > 0 && !tickerDismissed && (
        <NewsTicker announcements={announcements} onDismiss={() => setTickerDismissed(true)} />
      )}
      <Navbar onScrollToTrainings={scrollToTrainings} hasTickerAbove={announcements.length > 0 && !tickerDismissed} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <ConstellationNetwork nodeCount={65} />
        <MorphingBlobs colors={["#00F0FF", "#7000FF", "#FF00FF"]} />
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="AI Background" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/50 to-[#050505]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-20">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-center text-center lg:text-left">
            {/* ── Left: copy ─────────────────────────────────────── */}
            <div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
              >
                <span className="inline-flex items-center gap-2 border border-[#00F0FF]/30 bg-[#00F0FF]/5 text-[#00F0FF] text-xs font-bold px-4 py-2 tracking-widest uppercase mb-8">
                  <Zap size={12} /> India&apos;s Premier AI Training Platform
                </span>
              </motion.div>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight mb-6">
                <TextReveal>Master the Art</TextReveal>
                <TextReveal delay={0.15}>
                  of <GradientText className="font-black">Artificial</GradientText>
                </TextReveal>
                <TextReveal delay={0.3}>
                  <span className="text-[#7000FF]">Intelligence</span>
                </TextReveal>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="text-[#A1A1AA] text-lg sm:text-xl max-w-2xl mx-auto lg:mx-0 mb-10 font-dm-sans leading-relaxed"
              >
                Industry-leading AI courses taught by experts. Get hands-on with cutting-edge AI tools, build real projects, and earn certificates that matter.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.8 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <MagneticButton
                  data-testid="hero-explore-btn"
                  onClick={() => navigate("/courses")}
                  className="bg-[#00F0FF] text-black font-bold px-8 py-4 text-lg hover:bg-white transition-colors duration-300 flex items-center gap-2 justify-center font-outfit"
                >
                  Explore Courses <ArrowRight size={20} />
                </MagneticButton>
                <MagneticButton
                  onClick={() => navigate("/admin/login")}
                  className="border border-[#27272A] text-white font-bold px-8 py-4 text-lg hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-300 font-outfit"
                >
                  Admin Portal
                </MagneticButton>
              </motion.div>
            </div>

            {/* ── Right: floating-treasure hero video ───────────── */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="relative w-full max-w-xl mx-auto lg:max-w-none"
              data-testid="hero-vault-video-wrap"
            >
              {/* Glow */}
              <div
                className="absolute -inset-6 -z-10 rounded-[2.5rem] blur-3xl opacity-60 pointer-events-none"
                style={{ background: "radial-gradient(60% 60% at 50% 60%, rgba(0,240,255,0.35), rgba(112,0,255,0.18) 50%, transparent 80%)" }}
              />
              <HeroVaultVideo />
            </motion.div>
          </div>

          {/* ── ShansoLearn brand banner (full-width below) ─────── */}
          <ShansoLearnBanner />
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ChevronDown size={24} className="text-[#A1A1AA]" />
        </motion.div>
      </section>

      <GradientDivider />

      {/* ShansoTutor promo */}
      <TutorPromoCard />

      <GradientDivider />

      {/* Features Section */}
      <section id="features" className="py-24 px-6 relative overflow-hidden">
        <MorphingBlobs colors={["#00F0FF", "#7000FF"]} />
        <div className="max-w-6xl mx-auto">
          <ScrollSection className="text-center mb-16">
            <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Why Choose Us</span>
            <TextReveal delay={0.1}>
              <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-3">Built for the Future<br />of AI Careers</h2>
            </TextReveal>
          </ScrollSection>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {FEATURES.map((f, i) => (
              <motion.div key={i} variants={staggerItem}>
                <TiltCard className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-[#00F0FF]/40 transition-all duration-300 group h-full cursor-default">
                  <motion.div
                    className="w-12 h-12 bg-[#00F0FF]/10 flex items-center justify-center mb-4 group-hover:bg-[#00F0FF]/20 transition-colors"
                    whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.5 } }}
                  >
                    <f.icon size={24} className="text-[#00F0FF]" strokeWidth={1.5} />
                  </motion.div>
                  <h3 className="font-outfit font-bold text-white text-lg mb-2">{f.title}</h3>
                  <p className="text-[#A1A1AA] text-sm font-dm-sans leading-relaxed">{f.desc}</p>
                </TiltCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      <GradientDivider />

      {/* Courses Section */}
      <section id="courses" className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <ScrollSection className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">AI Courses</span>
              <TextReveal delay={0.1}>
                <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-3">Available Trainings</h2>
              </TextReveal>
            </div>
          </ScrollSection>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-zinc-900/50 border border-zinc-800 h-80 animate-pulse" />
              ))}
            </div>
          ) : trainings.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-zinc-800">
              <Brain size={48} className="text-zinc-700 mx-auto mb-4" />
              <p className="text-[#A1A1AA] text-lg font-dm-sans">No courses available yet. Check back soon!</p>
            </div>
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {trainings.map((t, i) => <CourseCard key={t.id} training={t} index={i} />)}
            </motion.div>
          )}
        </div>
      </section>
      <GradientDivider />

      {/* Document Purchase Section */}
      {books.length > 0 && (
        <section id="books" className="py-24 px-6 bg-[#0a0a0a] relative overflow-hidden">
          <div className="max-w-6xl mx-auto">
            <ScrollSection className="text-center mb-16">
              <span className="text-[#7000FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Digital Resources</span>
              <TextReveal delay={0.1}>
                <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-3">AI Learning Documents</h2>
              </TextReveal>
              <p className="text-[#A1A1AA] text-base mt-4 font-dm-sans max-w-2xl mx-auto">
                Comprehensive digital documents to deepen your AI knowledge
              </p>
            </ScrollSection>
            
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {books.map((book, i) => (
                <motion.div key={book.id} variants={staggerItem}>
                  <BookCard book={book} index={i} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}
      <GradientDivider />

      {/* AI News Section */}
      <section id="ai-news" className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <ScrollSection className="text-center mb-16">
            <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Stay Updated</span>
            <TextReveal delay={0.1}>
              <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-3">Latest AI News</h2>
            </TextReveal>
            <p className="text-[#A1A1AA] text-base mt-4 font-dm-sans max-w-2xl mx-auto">
              Stay ahead with the latest developments in artificial intelligence
            </p>
          </ScrollSection>
          
          {news.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {news.slice(0, 4).map((item, i) => (
                <motion.a
                  key={item.id || i}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="group bg-zinc-900/50 border border-zinc-800 overflow-hidden hover:border-[#00F0FF]/40 transition-all duration-300"
                  data-testid={`news-card-${i}`}
                >
                  {/* News Image */}
                  <div className="relative h-36 bg-gradient-to-br from-[#00F0FF]/10 to-[#7000FF]/10 overflow-hidden">
                    {item.image_url ? (
                      <img 
                        src={item.image_url} 
                        alt={item.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper size={32} className="text-[#00F0FF]/30" />
                      </div>
                    )}
                    {item.is_pinned && (
                      <span className="absolute top-2 left-2 bg-[#00F0FF] text-black text-[10px] font-bold px-2 py-0.5 uppercase">
                        Featured
                      </span>
                    )}
                  </div>
                  
                  {/* News Content */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#00F0FF] text-[10px] font-bold uppercase tracking-wide">
                        {item.source || 'AI News'}
                      </span>
                      {item.published_at && (
                        <span className="text-[#71717A] text-[10px]">
                          • {new Date(item.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                    <h3 className="text-white font-outfit font-bold text-sm leading-tight mb-2 group-hover:text-[#00F0FF] transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="text-[#A1A1AA] text-xs font-dm-sans leading-relaxed line-clamp-2">
                        {item.description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim().slice(0, 120)}
                      </p>
                    )}
                    <div className="flex items-center gap-1 mt-3 text-[#00F0FF] text-xs font-dm-sans font-semibold group-hover:gap-2 transition-all">
                      Read more <ExternalLink size={12} />
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          ) : (
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
                <Newspaper size={32} className="text-[#00F0FF]/30 mx-auto mb-4" />
                <p className="text-[#A1A1AA] text-base font-dm-sans">Loading latest AI news...</p>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 px-6 border-t border-[#27272A]">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Testimonials</span>
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-3">What Our Community Says</h2>
          </motion.div>
          
          {testimonials.length === 0 ? (
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
                <p className="text-[#A1A1AA] text-lg font-dm-sans">Testimonials will be updated soon</p>
              </div>
            </motion.div>
          ) : (
            <div>
              {/* Tabs */}
              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex justify-center mb-10">
                <div className="inline-flex bg-zinc-900/80 border border-zinc-800 p-1.5 gap-1">
                  <button
                    onClick={() => setActiveTestimonialTab("training")}
                    className={`flex items-center gap-2 px-5 py-2.5 font-dm-sans font-bold text-sm transition-all duration-300 ${
                      activeTestimonialTab === "training"
                        ? "bg-purple-500 text-white"
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                    }`}
                  >
                    <GraduationCap size={18} />
                    Training
                  </button>
                  <button
                    onClick={() => setActiveTestimonialTab("website")}
                    className={`flex items-center gap-2 px-5 py-2.5 font-dm-sans font-bold text-sm transition-all duration-300 ${
                      activeTestimonialTab === "website"
                        ? "bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-white"
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                    }`}
                  >
                    <Globe size={18} />
                    Website Services
                  </button>
                  <button
                    onClick={() => setActiveTestimonialTab("document")}
                    className={`flex items-center gap-2 px-5 py-2.5 font-dm-sans font-bold text-sm transition-all duration-300 ${
                      activeTestimonialTab === "document"
                        ? "bg-cyan-500 text-white"
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                    }`}
                  >
                    <FileText size={18} />
                    Document Purchase
                  </button>
                </div>
              </motion.div>

              {/* Training Reviews Tab */}
              {activeTestimonialTab === "training" && (() => {
                const trainingReviews = testimonials.filter(t => t.category === 'training' || t.category === 'general');
                const displayedReviews = showAllTrainingReviews ? trainingReviews : trainingReviews.slice(0, 4);
                
                if (trainingReviews.length === 0) {
                  return (
                    <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
                      <GraduationCap size={48} className="text-purple-500/30 mx-auto mb-4" />
                      <p className="text-[#A1A1AA] font-dm-sans">No training reviews yet</p>
                    </div>
                  );
                }
                
                return (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {displayedReviews.map((testimonial, idx) => (
                        <motion.div
                          key={testimonial.id}
                          custom={idx}
                          variants={fadeUp}
                          initial="hidden"
                          animate="visible"
                          className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-purple-500/40 transition-all duration-300 relative"
                        >
                          <Quote size={32} className="absolute top-4 right-4 text-purple-500/20" />
                          <div className="flex items-center gap-4 mb-4">
                            {testimonial.photo_url ? (
                              <img src={testimonial.photo_url} alt={testimonial.name} className="w-14 h-14 rounded-full object-cover border-2 border-purple-500/30" />
                            ) : (
                              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                                <User size={24} className="text-white" />
                              </div>
                            )}
                            <div>
                              <h4 className="font-outfit font-bold text-white">{testimonial.name}</h4>
                              {(testimonial.role || testimonial.company) && (
                                <p className="text-[#A1A1AA] text-sm font-dm-sans">
                                  {testimonial.role}{testimonial.role && testimonial.company && " at "}{testimonial.company}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 mb-3">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} size={16} className={star <= testimonial.rating ? "fill-amber-400 text-amber-400" : "text-zinc-600"} />
                            ))}
                          </div>
                          {testimonial.linked_item_name && (
                            <p className="text-purple-400 text-xs font-dm-sans font-semibold mb-2 flex items-center gap-1.5">
                              <GraduationCap size={12} /> {testimonial.linked_item_name}
                            </p>
                          )}
                          <p className="text-[#A1A1AA] text-sm font-dm-sans leading-relaxed line-clamp-4">"{testimonial.review_text}"</p>
                        </motion.div>
                      ))}
                    </div>
                    
                    {trainingReviews.length > 4 && (
                      <div className="mt-6 text-center">
                        <button
                          onClick={() => setShowAllTrainingReviews(!showAllTrainingReviews)}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500/10 border border-purple-500/30 text-purple-400 font-dm-sans font-bold hover:bg-purple-500/20 transition-all duration-300"
                        >
                          {showAllTrainingReviews ? (
                            <>Show Less <ChevronUp size={18} /></>
                          ) : (
                            <>View All {trainingReviews.length} Reviews <ChevronDown size={18} /></>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Website Services Reviews Tab */}
              {activeTestimonialTab === "website" && (() => {
                const websiteReviews = testimonials.filter(t => t.category === 'website');
                const displayedReviews = showAllWebsiteReviews ? websiteReviews : websiteReviews.slice(0, 4);
                
                if (websiteReviews.length === 0) {
                  return (
                    <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
                      <Globe size={48} className="text-[#00F0FF]/30 mx-auto mb-4" />
                      <p className="text-[#A1A1AA] font-dm-sans">No website services reviews yet</p>
                    </div>
                  );
                }
                
                return (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {displayedReviews.map((testimonial, idx) => (
                        <motion.div
                          key={testimonial.id}
                          custom={idx}
                          variants={fadeUp}
                          initial="hidden"
                          animate="visible"
                          className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-[#00F0FF]/40 transition-all duration-300 relative"
                        >
                          <Quote size={32} className="absolute top-4 right-4 text-[#00F0FF]/20" />
                          <div className="flex items-center gap-4 mb-4">
                            {testimonial.photo_url ? (
                              <img src={testimonial.photo_url} alt={testimonial.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#00F0FF]/30" />
                            ) : (
                              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#7000FF] flex items-center justify-center">
                                <User size={24} className="text-white" />
                              </div>
                            )}
                            <div>
                              <h4 className="font-outfit font-bold text-white">{testimonial.name}</h4>
                              {(testimonial.role || testimonial.company) && (
                                <p className="text-[#A1A1AA] text-sm font-dm-sans">
                                  {testimonial.role}{testimonial.role && testimonial.company && " at "}{testimonial.company}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 mb-3">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} size={16} className={star <= testimonial.rating ? "fill-amber-400 text-amber-400" : "text-zinc-600"} />
                            ))}
                          </div>
                          <p className="text-[#A1A1AA] text-sm font-dm-sans leading-relaxed line-clamp-4">"{testimonial.review_text}"</p>
                        </motion.div>
                      ))}
                    </div>
                    
                    {websiteReviews.length > 4 && (
                      <div className="mt-6 text-center">
                        <button
                          onClick={() => setShowAllWebsiteReviews(!showAllWebsiteReviews)}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] font-dm-sans font-bold hover:bg-[#00F0FF]/20 transition-all duration-300"
                        >
                          {showAllWebsiteReviews ? (
                            <>Show Less <ChevronUp size={18} /></>
                          ) : (
                            <>View All {websiteReviews.length} Reviews <ChevronDown size={18} /></>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Document Purchase Reviews Tab */}
              {activeTestimonialTab === "document" && (() => {
                const documentReviews = testimonials.filter(t => t.category === 'document');
                const displayedReviews = showAllDocumentReviews ? documentReviews : documentReviews.slice(0, 4);
                
                if (documentReviews.length === 0) {
                  return (
                    <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
                      <FileText size={48} className="text-cyan-500/30 mx-auto mb-4" />
                      <p className="text-[#A1A1AA] font-dm-sans">No document purchase reviews yet</p>
                    </div>
                  );
                }
                
                return (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {displayedReviews.map((testimonial, idx) => (
                        <motion.div
                          key={testimonial.id}
                          custom={idx}
                          variants={fadeUp}
                          initial="hidden"
                          animate="visible"
                          className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-cyan-500/40 transition-all duration-300 relative"
                        >
                          <Quote size={32} className="absolute top-4 right-4 text-cyan-500/20" />
                          <div className="flex items-center gap-4 mb-4">
                            {testimonial.photo_url ? (
                              <img src={testimonial.photo_url} alt={testimonial.name} className="w-14 h-14 rounded-full object-cover border-2 border-cyan-500/30" />
                            ) : (
                              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center">
                                <User size={24} className="text-white" />
                              </div>
                            )}
                            <div>
                              <h4 className="font-outfit font-bold text-white">{testimonial.name}</h4>
                              {(testimonial.role || testimonial.company) && (
                                <p className="text-[#A1A1AA] text-sm font-dm-sans">
                                  {testimonial.role}{testimonial.role && testimonial.company && " at "}{testimonial.company}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 mb-3">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} size={16} className={star <= testimonial.rating ? "fill-amber-400 text-amber-400" : "text-zinc-600"} />
                            ))}
                          </div>
                          {testimonial.linked_item_name && (
                            <p className="text-cyan-400 text-xs font-dm-sans font-semibold mb-2 flex items-center gap-1.5">
                              <FileText size={12} /> {testimonial.linked_item_name}
                            </p>
                          )}
                          <p className="text-[#A1A1AA] text-sm font-dm-sans leading-relaxed line-clamp-4">"{testimonial.review_text}"</p>
                        </motion.div>
                      ))}
                    </div>
                    
                    {documentReviews.length > 4 && (
                      <div className="mt-6 text-center">
                        <button
                          onClick={() => setShowAllDocumentReviews(!showAllDocumentReviews)}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 font-dm-sans font-bold hover:bg-cyan-500/20 transition-all duration-300"
                        >
                          {showAllDocumentReviews ? (
                            <>Show Less <ChevronUp size={18} /></>
                          ) : (
                            <>View All {documentReviews.length} Reviews <ChevronDown size={18} /></>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-24 px-6 border-t border-[#27272A]">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Get in Touch</span>
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-3">Contact Us</h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Trainer Card */}
            <motion.div 
              custom={0} 
              variants={fadeUp} 
              initial="hidden" 
              whileInView="visible" 
              viewport={{ once: true }}
              className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-[#00F0FF]/40 transition-all duration-300 group"
            >
              <div className="w-12 h-12 bg-[#00F0FF]/10 flex items-center justify-center mb-4 group-hover:bg-[#00F0FF]/20 transition-colors">
                <User size={24} className="text-[#00F0FF]" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit font-bold text-white text-lg mb-2">Trainer</h3>
              <p className="text-[#00F0FF] text-base font-dm-sans font-semibold">Shunmugavel</p>
              <p className="text-[#A1A1AA] text-sm font-dm-sans mt-2 leading-relaxed">Expert AI trainer with years of industry experience in machine learning and artificial intelligence.</p>
            </motion.div>

            {/* Email Card */}
            <motion.div 
              custom={1} 
              variants={fadeUp} 
              initial="hidden" 
              whileInView="visible" 
              viewport={{ once: true }}
              className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-[#00F0FF]/40 transition-all duration-300 group"
            >
              <div className="w-12 h-12 bg-[#00F0FF]/10 flex items-center justify-center mb-4 group-hover:bg-[#00F0FF]/20 transition-colors">
                <Mail size={24} className="text-[#00F0FF]" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit font-bold text-white text-lg mb-2">Email Us</h3>
              <a 
                href="mailto:support@shansoai.com" 
                className="text-[#00F0FF] text-base font-dm-sans font-semibold hover:underline break-all"
                data-testid="contact-email-link"
              >
                support@shansoai.com
              </a>
              <p className="text-[#A1A1AA] text-sm font-dm-sans mt-2 leading-relaxed">We respond within 24 hours on business days.</p>
            </motion.div>

            {/* Location Card */}
            <motion.div 
              custom={2} 
              variants={fadeUp} 
              initial="hidden" 
              whileInView="visible" 
              viewport={{ once: true }}
              className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-[#00F0FF]/40 transition-all duration-300 group"
            >
              <div className="w-12 h-12 bg-[#00F0FF]/10 flex items-center justify-center mb-4 group-hover:bg-[#00F0FF]/20 transition-colors">
                <MapPin size={24} className="text-[#00F0FF]" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit font-bold text-white text-lg mb-2">Location</h3>
              <p className="text-[#00F0FF] text-base font-dm-sans font-semibold">Tamil Nadu, India</p>
              <p className="text-[#A1A1AA] text-sm font-dm-sans mt-2 leading-relaxed">Offering both online and in-person AI training sessions.</p>
            </motion.div>
          </div>

          {/* Contact CTA */}
          <motion.div 
            variants={fadeUp} 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <Link 
              to="/contact"
              data-testid="contact-page-link"
              className="inline-flex items-center gap-2 border border-[#00F0FF]/30 bg-[#00F0FF]/5 text-[#00F0FF] text-sm font-bold px-6 py-3 hover:bg-[#00F0FF]/10 transition-colors duration-300 font-dm-sans"
            >
              <Mail size={16} /> Send Us a Message
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <MorphingBlobs colors={["#7000FF", "#00F0FF", "#FF00FF"]} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <ScrollSection>
            <TextReveal>
              <h2 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-6">
                Ready to Master <GradientText>AI?</GradientText>
              </h2>
            </TextReveal>
            <p className="text-[#A1A1AA] text-lg mb-10 font-dm-sans">Join thousands of professionals who have transformed their careers with ShansoAI Hub.</p>
            <MagneticButton
              data-testid="cta-enroll-btn"
              onClick={scrollToTrainings}
              className="bg-[#00F0FF] text-black font-outfit font-black px-12 py-5 text-xl hover:bg-white transition-colors duration-300 inline-flex items-center gap-3"
            >
              Start Learning Today <ArrowRight size={24} />
            </MagneticButton>
          </ScrollSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#27272A] pt-12 pb-6 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Top footer row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-10 border-b border-[#27272A]">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#00F0FF] rounded-sm flex items-center justify-center">
                  <Brain size={16} className="text-black" />
                </div>
                <span className="font-outfit font-bold text-[#00F0FF] text-lg">ShansoAI<span className="text-white">Hub</span></span>
              </div>
              <p className="text-[#A1A1AA] text-sm font-dm-sans leading-relaxed">
                India's premier AI training platform. Empowering professionals with industry-relevant AI skills and certified learning.
              </p>
            </div>
            {/* Quick Links */}
            <div>
              <h4 className="font-outfit font-bold text-white mb-4 text-sm tracking-widest uppercase">Quick Links</h4>
              <div className="space-y-2">
                <button onClick={scrollToTrainings} className="block text-[#A1A1AA] hover:text-[#00F0FF] text-sm font-dm-sans transition-colors">Browse Courses</button>
                <button onClick={() => navigate("/admin/login")} className="block text-[#A1A1AA] hover:text-[#00F0FF] text-sm font-dm-sans transition-colors">Admin Portal</button>
                <Link to="/contact" className="block text-[#A1A1AA] hover:text-[#00F0FF] text-sm font-dm-sans transition-colors">Contact Us</Link>
              </div>
            </div>
            {/* Legal */}
            <div>
              <h4 className="font-outfit font-bold text-white mb-4 text-sm tracking-widest uppercase">Legal</h4>
              <div className="space-y-2">
                <Link to="/privacy-policy" className="block text-[#A1A1AA] hover:text-[#00F0FF] text-sm font-dm-sans transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="block text-[#A1A1AA] hover:text-[#00F0FF] text-sm font-dm-sans transition-colors">Terms & Conditions</Link>
                <Link to="/refund-policy" className="block text-[#A1A1AA] hover:text-[#00F0FF] text-sm font-dm-sans transition-colors">Refund Policy</Link>
              </div>
            </div>
          </div>
          {/* Bottom bar */}
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[#A1A1AA] text-xs font-dm-sans">© {new Date().getFullYear()} ShansoAI Hub. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Link to="/privacy-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans transition-colors">Privacy</Link>
              <Link to="/terms" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans transition-colors">Terms</Link>
              <Link to="/refund-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans transition-colors">Refunds</Link>
              <Link to="/contact" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
